import { CheckCircle, Clock, GitFork, Minus, RefreshCw, Trash2, XCircle } from 'lucide-react';
import { useCallback, useState } from 'react';

import type { GroupEntry } from '../api/groups.js';
import { useDeleteGroup, useSyncGroup } from '../api/groups.js';
import { JobProgress } from '../components/job-progress.js';

const STATUS_CONFIG = {
  ok: { Icon: CheckCircle, cls: 'text-emerald-400', label: 'Synced' },
  error: { Icon: XCircle, cls: 'text-rose-400', label: 'Error' },
  pending: { Icon: Clock, cls: 'text-amber-400', label: 'Pending' },
  never: { Icon: Minus, cls: 'text-text-muted', label: 'Never synced' },
} as const;

interface GroupRowProps {
  readonly group: GroupEntry;
}

export function GroupRow({ group }: GroupRowProps): JSX.Element {
  const [jobId, setJobId] = useState<string | null>(null);
  const sync = useSyncGroup();
  const del = useDeleteGroup();

  const handleSync = (): void => {
    sync.mutate(group.name, { onSuccess: (id) => setJobId(id) });
  };

  const handleDelete = (): void => {
    if (!window.confirm(`Delete group "${group.name}"? This cannot be undone.`)) return;
    del.mutate(group.name);
  };

  const handleJobTerminal = useCallback(() => setJobId(null), []);

  const status = group.lastSyncStatus ?? 'never';
  const { Icon: StatusIcon, cls, label } = STATUS_CONFIG[status];

  return (
    <li className="rounded-2xl border border-border-subtle bg-surface/80 p-4 transition-all hover:border-accent/40 hover:bg-surface">
      <div className="flex flex-wrap items-center gap-3">
        <span className="grid h-8 w-8 flex-none place-items-center rounded-lg border border-border-subtle bg-deep text-node-folder">
          <GitFork className="h-3.5 w-3.5" />
        </span>
        <strong className="flex-1 text-base font-semibold text-text-primary">{group.name}</strong>
        <span className={`flex items-center gap-1 text-xs ${cls}`}>
          <StatusIcon className="h-3 w-3" />
          {label}
        </span>
        {group.lastContractCount != null && (
          <span className="font-mono text-[11px] text-text-muted">
            {group.lastContractCount} contracts
          </span>
        )}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={handleSync}
            disabled={sync.isPending || jobId !== null}
            title="Sync group"
            className="inline-flex items-center gap-1 rounded-lg border border-border-subtle px-2.5 py-1.5 text-xs text-text-secondary transition-all hover:border-accent/40 hover:text-accent disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${sync.isPending ? 'animate-spin' : ''}`} />
            Sync
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={del.isPending}
            title="Delete group"
            className="inline-flex items-center gap-1 rounded-lg border border-rose-500/30 px-2 py-1.5 text-xs text-rose-400 transition-all hover:bg-rose-500/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {group.members.map((m) => (
          <span
            key={m}
            className="inline-flex items-center rounded-full border border-border-subtle bg-elevated px-2.5 py-0.5 font-mono text-[11px] text-text-secondary"
          >
            {m}
          </span>
        ))}
      </div>
      {group.lastSyncedAt && (
        <p className="mt-2 font-mono text-[10px] text-text-muted">
          Last synced {new Date(group.lastSyncedAt).toLocaleString()}
        </p>
      )}
      {status === 'error' && group.lastError && (
        <p className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
          {group.lastError}
        </p>
      )}
      {jobId && (
        <div className="mt-3">
          <JobProgress jobId={jobId} onTerminal={handleJobTerminal} />
        </div>
      )}
    </li>
  );
}
